/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormikProps } from 'formik';
import {
  selectReportName,
  calculatorActions,
  selectIsLoading,
  selectTouched
} from '../../../redux/calculatorSlice';
import { selectUser } from '../../../redux/authSlice';
import { CircularButton, Dialog, ReportNameDialog } from '../../../components';
import { paths } from '../..';
import { Constants, StringFormatter } from '../../../utils';
import useStyles from './Footer.styles';

interface IProps {
  currentForm: FormikProps<any>;
}

const Footer: FunctionComponent<IProps> = ({
  currentForm
}) => {
  const classes = useStyles();
  const location = useLocation();
  const reportName = useSelector(selectReportName);
  const isLoading = useSelector(selectIsLoading);
  const touched = useSelector(selectTouched);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const [isConfirmReviewDialogOpen, setConfirmReviewDialogOpen] = useState(false);
  const [isSubmitReview, setIsSubmitReview] = useState(false);
  const selectedUser = useSelector(selectUser);
  const selectedUserName = String(selectedUser?.name);
  const selectedUserEmail = String(selectedUser?.email);
  const selectedUserNameOfSite = String(selectedUser?.['custom:name_of_site']);

  const pathOptions = {
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.calculatorInterventionsChecklist
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.calculatorServiceData
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.calculatorInterventions
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.calculatorInterventionsChecklist
      ),
    },
    [StringFormatter.replaceWithReportName(
      reportName,
      paths.calculatorReport
    )]: {
      backTo: StringFormatter.replaceWithReportName(
        reportName,
        paths.interventionsFoodSecurity
      ),
    },
  };

  const onFinishReportClick = () => {
    setIsSubmitReview(true);
    if (reportName) {
      dispatch(calculatorActions.saveReport({navigate:navigate,pathname:location.pathname, update: true, status: Constants.REPORT_STATUS.SUBMITTED }));
      dispatch(calculatorActions.newReportNotification(navigate,selectedUserName, selectedUserNameOfSite, selectedUserEmail));
    } else {
      setConfirmReviewDialogOpen(false);
      setIsTitleDialogOpen(true);
    }
  };

  const onSaveReport = (reportNameToSave: string) => { 
    dispatch(
      calculatorActions.saveReport({
        navigate:navigate,
        reportName: reportNameToSave,
        update: true,
        pathname:location.pathname,
        ...(isSubmitReview && { status: Constants.REPORT_STATUS.SUBMITTED }),
      }));
    if (isSubmitReview) {
      dispatch(calculatorActions.newReportNotification(navigate,selectedUserName, selectedUserNameOfSite, selectedUserEmail));
    }
    setIsTitleDialogOpen(false);
  };

  const onDialogConfirm = (confirmReportName: string) => {
    currentForm.validateForm().then((currentFormErrors) => {
      if (Object.keys(currentFormErrors).length === 0) {
        currentForm.submitForm();
        onSaveReport(confirmReportName);
      }
    });
  };

  const saveAndNext = () => {
    currentForm.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        if (!reportName) setIsTitleDialogOpen(true);
        else {
          currentForm.submitForm();
          onSaveReport(reportName);
        }
      }
    });
  };

  const onSaveAndNextClick = (event: React.FormEvent) => {
    if (touched) {
      event.preventDefault();
      saveAndNext();
    }
  };

  if (isLoading) return null;

  return (
    <footer className={classes.footer}>
      <ReportNameDialog
        isOpen={isTitleDialogOpen}
        onClose={() => setIsTitleDialogOpen(false)}
        inputCustomClass={classes.calculationsNameInput}
        onSave={onDialogConfirm}
      />
      <Dialog
        id="report__confirm-submit-report"
        title="Are you sure you want to submit this calculation for review?"
        open={isConfirmReviewDialogOpen}
        onClose={() => {
          setConfirmReviewDialogOpen(false);
        }}
        onSubmitBtnClick={onFinishReportClick}
        submitBtnText="Submit for Review"
        closeOnSubmit={false}
      >
        <p>
          Once submitted you will not be able to edit this calculation while it is being reviewed. 
          The HealthySteps National Office will reach out with next steps.
        </p>
      </Dialog>
      {pathOptions[decodeURIComponent(location.pathname)] && (
        <CircularButton
          type="button"
          color="primary"
          customClass={`${classes.button} ${classes.secondaryButton}`}
          onClick={() => {
            navigate(pathOptions[decodeURIComponent(location.pathname)].backTo);
          }}
        >
          Previous
        </CircularButton>
      )}
      {decodeURIComponent(location.pathname) ===
      StringFormatter.replaceWithReportName(
        reportName,
        paths.calculatorReport
      ) ? (
        <CircularButton
          type="button"
          color="primary"
          variant="contained"
          customClass={classes.reportButton}
          onClick={() => setConfirmReviewDialogOpen(true)}
        >
          Submit for Review
        </CircularButton>
      ) : (
        <CircularButton
          type="submit"
          color="primary"
          variant="contained"
          customClass={classes.button}
          onClick={onSaveAndNextClick}
        >
          Save & Next
        </CircularButton>
      )}
    </footer>
  );
};

export default Footer;
